import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useModal } from '@context/ModalsContext'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { removeUploadedFile } from '@ui/components/FileUpload/FileUpload'
import { FileData } from '@ui/components/FileUpload/types'

import { isDoubleSidedDocument } from '@utils/uploadDocument'

import { ClientNciPriorityDto } from '@model/ClientNciPriorityDto'
import { CreateClientDocument } from '@model/CreateClientDocuments'
import { ClientCreateNciFinalHistory, ClientNciHistoryStatus } from '@model/CreateNciHistory'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryDto } from '@model/DocumentDto'

import { createFormSelectField } from '@global/formField/FormSelectField'

import { ConfirmModal } from '@shared/modals/Confirm.modal'

import { DocumentFileUploadsFactory } from '../DocumentFileUploadsFactory'
import { DocumentConfirmationModal } from './modals/DocumentConfirmation.modal'

import styles from '../DocumentsInfoPage.module.scss'

export interface DocumentInfoPageFormValuesStep1 {
  documentTypeId: string
  documentCategoryType: string
  documents: DocumentManagementDocument[]
  reason?: string
  files: FileData[]
  nciDocumentNumber: string
}

interface DocumentManagementDocument {
  categoryId: number
  typeId: number
  file: string
  filename: string
  error: boolean
}

const FormSelectField = createFormSelectField<DocumentInfoPageFormValuesStep1>()

const DocumentInfoNonCiPageStep1FormUI: React.FC<
  FormikProps<DocumentInfoPageFormValuesStep1> & OuterProps
> = (props) => {
  const { setValues, values, onSkipDocuments, isValid, handleSubmit, documentCategoryTypes } = props

  const { t } = useTranslation()

  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const skipUploadModal = useModal(DocumentConfirmationModal, { onConfirm: onSkipDocuments })
  const deleteFileConfirmModal = useModal(ConfirmModal, {
    title: t('Sign up.Remove Document'),
    description: t('Sign up.Do you really want to delete this document?'),
    primaryLabel: t('Yes'),
    secondaryLabel: t('Cancel'),
  })

  const [file, setFile] = useState<FileData>()

  const handleSubmitClick = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setIsFormSubmitting(true)
      await handleSubmit()
    } catch (error: unknown) {
      console.log(error)
    } finally {
      setIsFormSubmitting(false)
    }
  }

  const handleConfirmationModalOpen = () => {
    skipUploadModal.open()
  }

  const handleDocumentUploads = (filesData: FileData[]) => {
    setValues({
      ...values,
      files: filesData,
      documents: filesData.map((x) => ({
        ...x,
        categoryId: DocumentCategoryType.Personal,
        typeId: Number.parseInt(values.documentCategoryType, 10),
        filename: x.fileName,
        file: x.base64Content,
        error: x.error ?? false,
        errorMessage: x.errorMessage ?? '',
      })),
    })
  }

  const handleDocumentRemove = () => {
    const files = removeUploadedFile(values.files, file)
    setValues({
      ...values,
      files,
      documents: files.map((x) => ({
        ...x,
        categoryId: DocumentCategoryType.Address,
        typeId: Number.parseInt(values.documentCategoryType, 10),
        filename: x.fileName,
        file: x.base64Content,
        error: x.error ?? false,
        errorMessage: x.errorMessage ?? '',
      })),
    })
  }

  const handleDocumentBeforeRemove = (file: FileData) => {
    setFile(file)
    deleteFileConfirmModal.open({ onConfirm: handleDocumentRemove })
  }

  useEffect(() => {
    props.setValues({ ...values, documents: [], files: [] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.documentTypeId])

  return (
    <React.Fragment>
      <Form onSubmit={handleSubmit}>
        <Heading6 medium>{t('Sign up.Identification')}</Heading6>
        <Body1 isParagraph>
          {t('Profile.Please upload an identification document displaying your full name Non NCI')}
        </Body1>

        <br />

        <div className='pb-4'>
          <FormSelectField
            name='documentTypeId'
            label={t('Sign up.Document Type')}
            required
            hasCheckMark={false}
            options={documentCategoryTypes.types.map((x) => ({
              id: x.id,
              name: x.name,
            }))}
          />
        </div>
        <div className='pb-5'>
          <div className='pb-5'>
            <div className='pb-3'>
              <DocumentFileUploadsFactory
                files={values.files}
                documentTypeId={values.documentTypeId}
                documentCategory={documentCategoryTypes.category.id}
                onChange={handleDocumentUploads}
                onBeforeRemove={handleDocumentBeforeRemove}
              />
            </div>
          </div>
        </div>

        <div className='is-flex is-justify-content-center'>
          <div className={styles.button}>
            <Button.Secondary size='lg' fullWidth onClick={handleConfirmationModalOpen}>
              {t('Skip')}
            </Button.Secondary>
            <Button.Primary
              size='lg'
              type='submit'
              fullWidth
              loading={isFormSubmitting}
              disabled={!isValid}
              onClick={handleSubmitClick}
            >
              {t('Submit')}
            </Button.Primary>
          </div>
        </div>
      </Form>
    </React.Fragment>
  )
}

interface OuterProps {
  accountNciAvailablePriorities: ClientNciPriorityDto

  documentCategoryTypes: DocumentCategoryDto

  onSkip(): void

  onSkipDocuments(): void

  handleSubmitReason(reason: string): void

  handleSubmit(values: ClientCreateNciFinalHistory, documents: CreateClientDocument[]): void
}

function dataToSubmit(
  values: DocumentInfoPageFormValuesStep1,
  accountNciAvailablePriorities: ClientNciPriorityDto
): ClientCreateNciFinalHistory {
  return {
    documentTypeId: Number.parseInt(values.documentTypeId, 10),
    nciDocumentNumber: values.nciDocumentNumber,
    nciPriorityId: accountNciAvailablePriorities.id,
    countryId: accountNciAvailablePriorities.country.id,
    statusId: ClientNciHistoryStatus.PENDING,
  }
}

export const DocumentInfoNonNciPageStep1Form = withFormik<
  OuterProps,
  DocumentInfoPageFormValuesStep1
>({
  mapPropsToValues: ({ accountNciAvailablePriorities, documentCategoryTypes }) => {
    const [documentType] = accountNciAvailablePriorities.documentTypes
    return {
      nciDocumentNumber: '',
      documentCategoryType: documentCategoryTypes.category.id.toString() || '',
      files: [],
      documentTypeId: documentType ? documentType.documentTypeId.toString() : '',
      documents: [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      props.handleSubmit(
        dataToSubmit(values, props.accountNciAvailablePriorities),
        values.documents.map((x) => ({
          ...x,
          typeId: Number.parseInt(values.documentTypeId, 10),
        }))
      )
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<DocumentInfoPageFormValuesStep1> = {}

    if (!values.documentCategoryType) {
      errors.documentCategoryType = t('Validation.Required')
    }

    const documents = values.documents.filter((x) => !!x?.filename).filter((x) => !x.error)
    if (
      documents.length <=
      (isDoubleSidedDocument(Number(values.documentCategoryType), Number(values.documentTypeId))
        ? 1
        : 0)
    ) {
      errors.documents = t('Validation.Required')
    }

    if (values.documents.some((doc) => doc.error)) {
      errors.documents = t('Validation.Required')
    }

    return errors
  },
  enableReinitialize: true,
  isInitialValid: false,
})(DocumentInfoNonCiPageStep1FormUI)
